import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { useStaticQuery, graphql } from "gatsby";
import styled from "styled-components";
import useVariation from "../../../hooks/useVariation";
import { Font, rem } from "../../../utils/style";
import { Color } from "../../../utils/styleDesignSystem";
import intl from "../../../services/intl";
import { getPromoDetailsForLocale } from "./promoDetailsData";

// Types
type Promotion = {
  expiresAt?: string;
  slug: string;
};

type PromotionDetailsInfoProps = {
  promotion: Promotion;
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
`;

const PromoList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PromoItem = styled.div`
  padding: var(--spacing-1_5) 0;
  border-bottom: 0.5px solid var(--cool-60, #DBE2E6);

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: var(--spacing-1);
  }
`;

const PromoImageWrapper = styled.div`
  width: 100%;
  margin-bottom: var(--spacing-0_5);
  border-radius: 8px;
  overflow: hidden;
  background-color: ${Color.darkSkyBlue20};
`;

const PromoImage = styled(GatsbyImage)`
  width: 100%;
  img {
    transition: none !important;
  }
`;

const PromoHeadline = styled.h4`
  color: ${Color.indigoBlue};
  margin: 0 0 var(--spacing-0_5) 0;
`;

const DurationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const EndDate = styled.span`
  ${Font.circular}
  font-size: ${rem(14)};
  line-height: ${rem(21)};
  color: ${Color.indigoBlue};

  em {
    ${Font.dutch}
  }
`;

const DaysLeftBadge = styled.span`
  ${Font.circular}
  font-size: ${rem(12)};
  font-weight: 300;
  line-height: ${rem(18)};
  color: ${Color.indigoBlue};
  background-color: var(--cool-40,#EAEEF0);
  padding: 2px 8px;
  border-radius: 4px;
`;

const PromoDescription = styled.p`
  color: ${Color.indigoBlue};
  margin: 0;
`;

const Disclaimer = styled.div`
  padding-top: var(--spacing-1);
  padding-left: 16px;
  padding-right: 16px;
  margin-left: -16px;
  margin-right: -16px;
  border-top: 0.5px solid var(--cool-60, #DBE2E6);

  p {
    font: var(--font-circular);
    font-weight: 300;
    color: ${Color.indigoBlue60};
    margin: 0;
    font-size: var(--spacing-0_75, 12px);
  }
`;

// Helper function to calculate days left
function getDaysLeft(expiresAt?: string): number | null {
  if (!expiresAt) return null;

  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) - 1;

  return diffDays > 0 ? diffDays : null;
}

// GraphQL query for Contentful images
const usePromoImages = () => {
  return useStaticQuery(graphql`
    fragment promoDetailsImageFragment on ContentfulAsset {
      description
      gatsbyImageData(
        layout: CONSTRAINED
        placeholder: DOMINANT_COLOR
        width: 600
        quality: 90
      )
    }

    query PromotionDetailsImagesQuery {
      promoDetails40Off: contentfulAsset(
        contentful_id: { eq: "4SlTybw0b3m6TpQnwesxJA" }
      ) {
        ...promoDetailsImageFragment
      }
      promoDetails25Off: contentfulAsset(
        contentful_id: { eq: "2mqCeAUQK76Ke2K50yHzk6" }
      ) {
        ...promoDetailsImageFragment
      }
      sculptSocietyImage: contentfulAsset(
        contentful_id: { eq: "5fVXNlwjnLuOmUl60ikfNb" }
      ) {
        ...promoDetailsImageFragment
      }
      oseaLipBoosterImage: contentfulAsset(
        contentful_id: { eq: "7byQY36IVJbBN3UjhXx2CH" }
      ) {
        ...promoDetailsImageFragment
      }
    }
  `);
};

type ImageQueryResult = {
  promoDetails40Off: {
    description: string;
    gatsbyImageData: IGatsbyImageData;
  };
  promoDetails25Off: {
    description: string;
    gatsbyImageData: IGatsbyImageData;
  };
  sculptSocietyImage: {
    description: string;
    gatsbyImageData: IGatsbyImageData;
  };
  oseaLipBoosterImage: {
    description: string;
    gatsbyImageData: IGatsbyImageData;
  };
};

const PromotionDetailsInfo = ({ promotion }: PromotionDetailsInfoProps) => {
  const images: ImageQueryResult = usePromoImages();
  const enabledNYS26OseaOOSFlag = useVariation("nys-26-osea-oos", false);
  const daysLeft = getDaysLeft(promotion?.expiresAt);
  const hideOseaPromo = promotion?.slug === "nys26-customer" || enabledNYS26OseaOOSFlag;
  const endDate = promotion?.expiresAt
    ? intl.formatDateTime(promotion.expiresAt, { month: "short", day: "numeric" })
    : null;

  // Get locale-specific promo data
  const locale = intl.locale;
  const { discountPromoDetails, promoDetails, disclaimerText } =
    getPromoDetailsForLocale(locale);

  // Add the matching discount promo as the first element
  const discountPromo = discountPromoDetails[promotion?.slug];
  const promoList = discountPromo
    ? [discountPromo, ...promoDetails]
    : promoDetails;

  return (
    <Container>
      <PromoList>
        {promoList.map((promo) => {
          const imageData = promo.imageKey ? images[promo.imageKey] : undefined;
          if (hideOseaPromo && promo.slug === "osea-lip-booster") {
            return null;
          }

          return (
            <PromoItem key={promo.slug}>
              {imageData?.gatsbyImageData && (
                <PromoImageWrapper>
                  <PromoImage
                    image={imageData.gatsbyImageData}
                    alt={imageData.description || promo.headline}
                  />
                </PromoImageWrapper>
              )}
              <PromoHeadline className="typography-label1">{promo.headline}</PromoHeadline>
              <DurationRow>
                <EndDate className="typography-label2">
                  {promo.expiresAt ? (
                    promo.expiresAt
                  ) : (
                    <>
                      <em className="typography-label2">Ends</em> {endDate}
                    </>
                  )}
                </EndDate>
                {!promo.expiresAt && daysLeft !== null && (
                  <DaysLeftBadge>
                    {intl.t("promo.details.days-left", "{count} days left", {
                      count: daysLeft,
                    })}
                  </DaysLeftBadge>
                )}
              </DurationRow>
              <PromoDescription className="typography-body2">{promo.description}</PromoDescription>
            </PromoItem>
          );
        })}
      </PromoList>
      <Disclaimer>
        <p className="typography-caption">{disclaimerText}</p>
      </Disclaimer>
    </Container>
  );
};

export default PromotionDetailsInfo;

